import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Radio, Eye } from "lucide-react";
import { LiveCard } from "../components/Cards";
import LivePlayerModal from "../components/LivePlayerModal";
import LiveStreamPlayer from "../components/LiveStreamPlayer";
import { SiteWatchBadge, YoutubeWatchButton } from "../components/WatchActions";
import { getYoutubeEmbed } from "../lib/api";
import { getLiveViewerCount } from "../lib/liveViewers";
import { useLivePresence, useLiveStreamsPoll } from "../hooks/useLiveViewers";

export default function Live() {
  const { streams, main } = useLiveStreamsPoll();
  const [active, setActive] = useState(null);

  const mainEmbed = main?.embedUrl ? getYoutubeEmbed(main.embedUrl, true) : null;
  const showMainPlayer = Boolean(mainEmbed && main?.isActive);
  const presenceViewers = useLivePresence(main?.id, showMainPlayer);
  const mainViewers = presenceViewers || getLiveViewerCount(main);

  const others = streams.filter((s) => !s.isMain);

  return (
    <div>
      <section className="pt-[74px]">
        <div className="tv-container py-10">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-6"
          >
            <h1 className="font-display font-extrabold text-4xl md:text-5xl text-white flex items-center gap-3">
              <span className="tv-badge-live text-sm">
                <span className="w-2 h-2 rounded-full bg-white live-dot" /> CANLI
              </span>
              Canlı Yayın
            </h1>
            <p className="text-[var(--tv-muted)] mt-3 max-w-2xl">
              Ana yayını sitede izleyin veya YouTube üzerinden takip edin.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            className="tv-card !overflow-hidden"
          >
            <div className="relative aspect-video bg-black">
              {showMainPlayer ? (
                <LiveStreamPlayer stream={main} autoplay />
              ) : (
                <LiveStreamPlayer stream={main || { title: "Canlı Yayın" }} autoplay={false} />
              )}
            </div>
            {main && (
              <div className="p-5 md:p-6">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      {main.isActive ? (
                        <span className="tv-badge-live">
                          <span className="w-1.5 h-1.5 rounded-full bg-white live-dot" /> CANLI
                        </span>
                      ) : (
                        <span className="tv-badge">YAYIN DIŞI</span>
                      )}
                      <span className="flex items-center gap-1.5 text-xs text-[var(--tv-muted)]">
                        <Eye size={13} className="text-[var(--tv-purple)]" />
                        <span className="text-white font-medium tabular-nums">{mainViewers}</span>
                        <span>izleyici</span>
                      </span>
                    </div>
                    <h2 className="text-xl font-bold text-white font-display flex items-center gap-2">
                      <Radio size={18} className="text-[var(--tv-purple-bright)]" /> {main.title}
                    </h2>
                    <p className="text-[var(--tv-muted)] text-sm mt-1">{main.description}</p>
                  </div>
                </div>
                {main.embedUrl && (
                  <div className="flex flex-wrap items-center gap-3 mt-5 pt-4 border-t border-[var(--tv-border)]">
                    {showMainPlayer && <SiteWatchBadge />}
                    <YoutubeWatchButton url={main.embedUrl} />
                  </div>
                )}
              </div>
            )}
          </motion.div>
        </div>
      </section>

      <div className="tv-container pb-16">
        <h2 className="tv-section-title text-white mb-6">
          <span className="tv-section-bar" /> Diğer Canlı Yayınlar
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
          {others.map((s, i) => <LiveCard key={s.id} stream={s} index={i} onOpen={setActive} />)}
        </div>
      </div>

      <LivePlayerModal open={!!active} stream={active} onClose={() => setActive(null)} />
    </div>
  );
}
